/**
 * useIdleDetection Hook
 * Detects when user is idle (no mouse movement, keyboard activity, etc.)
 * Pauses API polling when user is inactive to save CU usage
 */

import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook to detect user idle state
 * @param {number} idleTimeMs - Time in milliseconds before considered idle (default: 60000 = 1 minute)
 * @returns {boolean} True if user is idle
 */
export const useIdleDetection = (idleTimeMs = 60000) => {
  const [isIdle, setIsIdle] = useState(false);
  const idleTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  useEffect(() => {
    const resetIdleTimer = () => {
      lastActivityRef.current = Date.now();
      if (isIdle) {
        setIsIdle(false);
        console.log('👤 User active - resuming API calls');
      }
      
      // Clear existing timer
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
      
      // Set new timer
      idleTimerRef.current = setTimeout(() => {
        setIsIdle(true);
        console.log('😴 User idle - pausing API calls to save CU');
      }, idleTimeMs);
    };

    // Track mouse movement
    const handleMouseMove = () => resetIdleTimer();
    const handleMouseClick = () => resetIdleTimer();
    const handleKeyPress = () => resetIdleTimer();
    const handleScroll = () => resetIdleTimer();
    const handleTouch = () => resetIdleTimer();

    // Track page visibility (tab switching)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsIdle(true);
        console.log('👁️ Tab hidden - pausing API calls');
      } else {
        resetIdleTimer();
      }
    };

    // Track window focus
    const handleFocus = () => resetIdleTimer();
    const handleBlur = () => {
      setIsIdle(true);
      console.log('🔍 Window blurred - pausing API calls');
    };

    // Add event listeners
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mousedown', handleMouseClick);
    window.addEventListener('keydown', handleKeyPress);
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('touchstart', handleTouch, { passive: true });
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    // Initialize timer
    resetIdleTimer();

    // Cleanup
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mousedown', handleMouseClick);
      window.removeEventListener('keydown', handleKeyPress);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('touchstart', handleTouch);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    };
  }, [idleTimeMs, isIdle]);

  return isIdle;
};

